import { FileSpreadsheet, FileText, Paperclip, Presentation, Upload, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"

/**
 * Los archivos elegidos antes de subirlos — de una tarea, de una orden de pago o de
 * lo que venga. **No sabe a qué cuelgan**: quien lo usa decide adónde se suben.
 *
 * ⚠ **Lo que se rechaza aquí es una cortesía, no la seguridad.** Quien decide es
 * `services/almacenamiento.py`: extensión en lista blanca, ≤ 10 MB, y para los tres
 * formatos de Office abrir el ZIP y exigir su parte propia. Esta comprobación existe
 * para no subir 8 MB a un `415` seguro, y **se mantiene igual de estrecha que la del
 * servidor a propósito**: aflojarla aquí no admite nada, sólo mueve el rechazo más
 * tarde y más caro.
 *
 * ⚠ **SVG no está, y no es un olvido.** Un SVG es XML con `<script>` dentro: es un
 * vector de XSS servido desde el propio dominio. Lo mismo con `.xls`, `.doc` y
 * `.ppt` —contenedores OLE donde la macro vive en un flujo interno— y con las
 * variantes `.xlsm`, `.docm` y `.pptm`, que son macros por definición.
 *
 * ⚠ **Las tres vías de siempre: elegir, soltar y Ctrl+V.** Un soporte llega como
 * llega —el comprobante se arrastra desde la carpeta de descargas, la captura del
 * pago se pega recién hecha—, y obligar a pasar por el selector de archivos para
 * algo que ya está en el portapapeles es trabajo de más varias veces al día. El
 * portapapeles entrega un blob sin nombre, así que se le pone uno antes de mandarlo.
 *
 * ⚠ **Soltar fuera de la zona no hace nada, y el navegador tampoco.** Sin el
 * `preventDefault` del `dragover`, soltar un PDF sobre la página lo ABRE y se pierde
 * el formulario a medio llenar. Por eso la zona cancela el arrastre entero.
 *
 * ⚠ **El rechazo se dice llegue por donde llegue el archivo.** Por eso la lista y su
 * motivo viven en quien usa este componente y no aquí dentro: el `.exe` puede entrar
 * por el selector o por un Ctrl+V, y con el estado aquí el pegado se tragaba el
 * archivo sin decir una palabra — que es peor que rechazarlo.
 *
 * ⚠ **Pulsar un adjunto lo abre en la vista previa**, antes de subirlo: la captura
 * pegada con Ctrl+V es a menudo la equivocada —la del pago anterior— y la miniatura de
 * 40 px no deja leer ni el monto ni la referencia.
 *
 * ⚠ **La miniatura se revoca.** `createObjectURL` retiene el archivo entero en
 * memoria hasta que se revoca; sin el `return` del efecto, adjuntar y quitar diez
 * capturas deja diez imágenes vivas hasta recargar la página.
 */

export type Adjuntando = { id: string; archivo: File }

/** Lo mismo que la lista blanca del servidor, en el orden en que él la escribe. */
export const EXTENSIONES_ADMITIDAS = [
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "xlsx",
  "docx",
  "pptx",
] as const

export const ACEPTA = EXTENSIONES_ADMITIDAS.map((e) => `.${e}`).join(",")

const MAXIMO_BYTES = 10 * 1024 * 1024

function extensionDe(nombre: string): string {
  const punto = nombre.lastIndexOf(".")
  return punto === -1 ? "" : nombre.slice(punto + 1).toLowerCase()
}

function kilobytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(0)} KB`
}

/**
 * Reparte los archivos entre los que el servidor aceptaría y los que no, con el
 * motivo de cada rechazo para poder decirlo en pantalla.
 */
export function repartir(archivos: File[]): { admitidos: Adjuntando[]; rechazados: string[] } {
  const admitidos: Adjuntando[] = []
  const rechazados: string[] = []
  for (const archivo of archivos) {
    const extension = extensionDe(archivo.name)
    if (!(EXTENSIONES_ADMITIDAS as readonly string[]).includes(extension)) {
      rechazados.push(`${archivo.name}: ese tipo de archivo no se admite`)
    } else if (archivo.size > MAXIMO_BYTES) {
      rechazados.push(`${archivo.name}: pasa de 10 MB`)
    } else {
      admitidos.push({ id: crypto.randomUUID(), archivo })
    }
  }
  return { admitidos, rechazados }
}

/** Añade lo admisible a lo que ya había, y devuelve el motivo de cada rechazo. */
export function sumar(
  previos: Adjuntando[],
  archivos: File[],
): { adjuntos: Adjuntando[]; rechazados: string[] } {
  const { admitidos, rechazados } = repartir(archivos)
  return { adjuntos: [...previos, ...admitidos], rechazados }
}

/**
 * Los archivos que trae un pegado, ya con nombre.
 *
 * Devuelve lista vacía cuando lo pegado era texto — y entonces **no** se corta el
 * pegado por defecto, porque pegar el número de expediente en el título es lo que
 * más se hace en este formulario.
 */
export function archivosPegados(datos: DataTransfer | null, desde: number): File[] {
  if (datos === null) return []
  return [...datos.items]
    .filter((i) => i.kind === "file")
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null)
    .map((f, i) =>
      f.name === "" ? new File([f], `captura-${desde + i + 1}.png`, { type: f.type }) : f,
    )
}

function Icono({ nombre }: { nombre: string }) {
  const extension = extensionDe(nombre)
  if (extension === "xlsx") return <FileSpreadsheet size={20} aria-hidden={true} />
  if (extension === "pptx") return <Presentation size={20} aria-hidden={true} />
  return <FileText size={20} aria-hidden={true} />
}

type Props = {
  adjuntos: Adjuntando[]
  /** Los motivos del último rechazo, ya redactados por `sumar`. */
  rechazados: string[]
  onArchivos: (archivos: File[]) => void
  onQuitar: (id: string) => void
  /** El texto del botón. Un comprobante de pago no es «un soporte» cualquiera. */
  etiqueta?: string
  /** Falso cuando sólo cabe uno: el comprobante del banco es uno. */
  multiple?: boolean
}

export function AdjuntosNuevos({
  adjuntos,
  rechazados,
  onArchivos,
  onQuitar,
  etiqueta = "Adjuntar soportes",
  multiple = true,
}: Props) {
  const entrada = useRef<HTMLInputElement>(null)
  const [miniaturas, setMiniaturas] = useState<Record<string, string>>({})
  const [encima, setEncima] = useState(false)
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)

  useEffect(() => {
    const urls: Record<string, string> = {}
    for (const a of adjuntos) {
      if (a.archivo.type.startsWith("image/")) urls[a.id] = URL.createObjectURL(a.archivo)
    }
    setMiniaturas(urls)
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url)
    }
  }, [adjuntos])

  const borde = { borderColor: "var(--border)" }

  return (
    <div className="mt-3">
      {/* La zona de soltar. No es un botón a propósito: el botón de dentro es el
          que abre el selector y el que alcanza el teclado, y anidar dos controles
          dejaría uno inalcanzable. Lleva `aria-label` porque con manejadores de
          arrastre un contenedor sin nombre es una zona que nadie anuncia. */}
      <section
        // Nombre propio, distinto del botón: si la zona y el botón se llamaran
        // igual, quien navega con lector oiría dos veces lo mismo sin saber en
        // cuál de los dos está.
        aria-label="Zona para soltar archivos"
        data-testid="zona-adjuntos"
        onDragOver={(e) => {
          // Sin esto el navegador no deja soltar, y al soltar ABRE el archivo.
          e.preventDefault()
          setEncima(true)
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          e.preventDefault()
          setEncima(false)
          const soltados = [...e.dataTransfer.files]
          if (soltados.length > 0) onArchivos(multiple ? soltados : soltados.slice(0, 1))
        }}
        className="flex flex-col items-center gap-2 rounded-[var(--radius-control)] border border-dashed p-4 text-center transition-colors"
        style={{
          borderColor: encima ? "var(--primary)" : "var(--border)",
          background: encima ? "var(--cristal-relleno)" : undefined,
        }}
      >
        <button
          type="button"
          onClick={() => entrada.current?.click()}
          className="boton-secundario flex items-center gap-2 px-3 py-1.5 text-sm"
        >
          <Upload size={14} aria-hidden={true} />
          {etiqueta}
        </button>
        <p className="flex items-center gap-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          <Paperclip size={12} aria-hidden={true} />
          Arrastra aquí, pega con Ctrl+V o elige. PDF, Excel, Word, PowerPoint o imagen — hasta 10
          MB.
        </p>
      </section>

      {/* Fuera del flujo, no `display:none`: un input oculto con `hidden` no abre el
          selector en algunos navegadores, y con `sr-only` sigue siendo alcanzable
          por teclado desde el botón. */}
      <input
        ref={entrada}
        type="file"
        multiple={multiple}
        accept={ACEPTA}
        aria-label={etiqueta}
        className="sr-only"
        onChange={(e) => {
          onArchivos([...(e.target.files ?? [])])
          // Sin esto, elegir el mismo archivo dos veces seguidas no dispara `change`.
          e.target.value = ""
        }}
      />

      {rechazados.length > 0 && (
        <ul
          role="alert"
          className="mt-2 space-y-0.5 text-xs"
          style={{ color: "var(--destructive)" }}
        >
          {rechazados.map((motivo) => (
            <li key={motivo}>{motivo}</li>
          ))}
        </ul>
      )}

      {adjuntos.length > 0 && (
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {adjuntos.map((a) => (
            <li
              key={a.id}
              data-testid={`adjunto-nuevo-${a.archivo.name}`}
              className="flex items-center gap-3 rounded-[var(--radius-control)] border p-2"
              style={borde}
            >
              <button
                type="button"
                className="foco-cristal flex min-w-0 flex-1 items-center gap-3 text-left"
                aria-label={`Ver ${a.archivo.name}`}
                onClick={() => setViendo({ local: a.archivo, nombre: a.archivo.name })}
              >
                {miniaturas[a.id] === undefined ? (
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border"
                    style={{ ...borde, color: "var(--fg-muted)" }}
                  >
                    <Icono nombre={a.archivo.name} />
                  </span>
                ) : (
                  <img
                    src={miniaturas[a.id]}
                    alt={`Vista previa de ${a.archivo.name}`}
                    className="h-10 w-10 shrink-0 rounded-[var(--radius-control)] border object-cover"
                    style={borde}
                  />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs">{a.archivo.name}</span>
                  <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {kilobytes(a.archivo.size)}
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label={`Quitar ${a.archivo.name}`}
                className="shrink-0"
                style={{ color: "var(--fg-muted)" }}
                onClick={() => onQuitar(a.id)}
              >
                <X size={14} aria-hidden={true} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </div>
  )
}
